import { useMemo } from "react";
import orderBy from "lodash/orderBy";
import { OnChangeFn } from "@tanstack/react-table";
import { useTable } from "hooks/useTable";
import { IProduct } from "integrations/firebase/collections";
import { useTranslation } from "react-i18next";
import { useProductAvailability } from "hooks/useProductAvailability";
import { useFeatureFlags } from "contexts/FeatureFlagContext";
import { BooksTable } from "components/BooksTable";
import { InputWithClear } from "components/input/inputWithClear";
import { useUrlSorting } from "hooks/useUrlSorting";
import { productMatchesQuery } from "../utils";
import { useAmplifyStatus } from "../hooks/useAmplifyStatus";
import { columns } from "./columns";
import { PreHeader } from "./PreHeader";

export const DetailsTable = ({
  onRowClick,
  isLoading,
  products,
  projectProductIds,
  searchQuery,
  onSearchQueryChange,
}: {
  onRowClick: (product: IProduct) => void;
  isLoading: boolean;
  products: IProduct[];
  projectProductIds: string[];
  searchQuery: string;
  onSearchQueryChange: OnChangeFn<string>;
}) => {
  const { t } = useTranslation(["projectDetails"]);
  const { isProductActive } = useProductAvailability();
  const { features } = useFeatureFlags();

  const sortedProducts = useMemo(
    () => orderBy(products ?? [], "isbn"),
    [products],
  );

  // The project's ids, not the loaded products': those arrive in batches, and
  // each new set of ids would be another request to Amplify.
  const aPlusStatus = useAmplifyStatus(projectProductIds);

  const tableColumns = useMemo(
    () =>
      columns(t, {
        isProductInactive: (product) =>
          features.inactiveBooksFilter &&
          !isProductActive(product.supplyDetails),
        aPlusStatus,
      }),
    [t, features.inactiveBooksFilter, isProductActive, aPlusStatus],
  );

  const sortableColumnIds = useMemo(
    () =>
      tableColumns
        .filter(
          (column) =>
            column.enableSorting !== false && column.ignoreColumnClick !== true,
        )
        .map((column) => {
          if ("accessorKey" in column && typeof column.accessorKey === "string")
            return column.accessorKey;

          return column.id;
        })
        .filter((columnId): columnId is string => typeof columnId === "string"),
    [tableColumns],
  );
  const { sorting, setSorting } = useUrlSorting({
    validColumnIds: sortableColumnIds,
  });

  const { table } = useTable({
    enableRowSelection: (row) => !row.original.nonExistent,
    columns: tableColumns,
    data: sortedProducts,
    totalRecords: products?.length || 0,
    getRowId: (row) => row.id,
    globalFilterFn: (row, _columnId, filterValue) =>
      productMatchesQuery(row.original, String(filterValue)),
    globalFilter: searchQuery,
    setGlobalFilter: onSearchQueryChange,
    enableSorting: true,
    enableMultiSort: false,
    sortDescFirst: false,
    sorting,
    setSorting,
  });

  return (
    <div data-testid="details-table">
      <BooksTable
        table={table}
        isLoading={isLoading}
        onBookClick={onRowClick}
        actions={<PreHeader table={table} />}
        header={
          <InputWithClear
            testId="project-search-input"
            placeholder={t("projectDetails:search.placeholder")}
            aria-label={t("projectDetails:search.placeholder")}
            value={searchQuery}
            onChange={(event) => table.setGlobalFilter(event.target.value)}
            onClear={() => table.setGlobalFilter("")}
            className="h-full rounded-lg"
            showClearOnHover
          />
        }
      />
    </div>
  );
};
